import React, { useState, useEffect } from 'react'
import { Folder, CalendarCheck, Plus, X, Loader, Pencil } from "lucide-react";
import norra16 from "../../images/Norra16-2.jpg";
import { useParams } from 'react-router-dom';
import { useAuth } from 'AuthContext';
import { apiFetch } from 'api';
import DropdownSelect from 'components/Dropdown/DropdownSelect';
import './Info.sass'


const clubs = [
    { name: "Halmstad GK", id: "000" },
    { name: "Ringenäs GK", id: "111" },
    { name: "PGA National", id: "222" },
];

const courses = {
    "Halmstad GK": [{ name: "Norra", id: "000" }, { name: "Södra", id: "111" }],
    "Ringenäs GK": [{ name: "Gul/Blå", id: "000" }, { name: "Blå/Röd", id: "111" }],
    "PGA National": [{ name: "Links", id: "000" }, { name: "Lakes", id: "111" }],
};

const tees = {
    "Norra": [{ name: "Ej bestämt", id: "none" }, { name: "Svart", id: "001" }, { name: "Vit", id: "002" }, { name: "Gul", id: "003" }],
    "Södra": [{ name: "Ej bestämt", id: "none" }, { name: "Vit", id: "001" }, { name: "Gul", id: "002" }],
    "Gul/Blå": [{ name: "Ej bestämt", id: "none" }, { name: "Vit", id: "001" }, { name: "Gul", id: "002" }],
    "Blå/Röd": [{ name: "Ej bestämt", id: "none" }, { name: "Vit", id: "001" }, { name: "Gul", id: "002" }],
    "Links": [{ name: "Ej bestämt", id: "none" }, { name: "68", id: "001" }, { name: "65", id: "002" }, { name: "61", id: "003" }, { name: "58", id: "004" }],
    "Lakes": [{ name: "Ej bestämt", id: "none" }, { name: "68", id: "001" }, { name: "65", id: "002" }, { name: "61", id: "003" }, { name: "58", id: "004" }],
};

const emptyRound = { name: '', date: '', firstTeeTime: '' };

function findClub(name) { return clubs.find(c => c.name === name) || {}; }
function findCourse(clubName, courseName) { return (courses[clubName] || []).find(c => c.name === courseName) || {}; }
function findTee(courseName, teeName) { return (tees[courseName] || []).find(t => t.name === teeName) || {}; }

export default function Info() {
    const { leagueId } = useParams();
    const { membership } = useAuth();
    const isAdmin = membership?.role === 'admin';

    const [upcomingRounds, setUpcomingRounds] = useState([]);
    const [documents, setDocuments] = useState([]);

    const [showRoundForm, setShowRoundForm] = useState(false);
    const [showDocForm, setShowDocForm] = useState(false);
    const [editingRound, setEditingRound] = useState(null);

    const [roundForm, setRoundForm] = useState(emptyRound);
    const [club, setClub] = useState({});
    const [course, setCourse] = useState({});
    const [tee, setTee] = useState({});

    const [docFile, setDocFile] = useState(null);
    const [savingRound, setSavingRound] = useState(false);
    const [savingDoc, setSavingDoc] = useState(false);
    const [roundError, setRoundError] = useState(null);

    const today = new Date().toISOString().split('T')[0];
    const isPast = (date) => date && date < today;
    const byDate = (a, b) => {
        const aPast = isPast(a.date);
        const bPast = isPast(b.date);
        if (aPast !== bPast) return aPast ? 1 : -1;
        return (a.date || '').localeCompare(b.date || '');
    };

    useEffect(() => {
        apiFetch(`/league/${leagueId}/upcoming-rounds`)
            .then(r => r.json())
            .then(data => setUpcomingRounds([...data].sort(byDate)))
            .catch(console.error);

        apiFetch(`/league/${leagueId}/documents`)
            .then(r => r.json())
            .then(setDocuments)
            .catch(console.error);
    }, [leagueId]);

    const handleClubChange = (item) => {
        setClub(item);
        setCourse({});
        setTee({});
    };

    const handleCourseChange = (item) => {
        setCourse(item);
        setTee({});
    };

    const resetRoundForm = () => {
        setRoundForm(emptyRound);
        setClub({});
        setCourse({});
        setTee({});
        setEditingRound(null);
        setRoundError(null);
    };

    const handleEditClick = (r) => {
        const foundClub = findClub(r.club);
        const foundCourse = findCourse(foundClub.name, r.course);
        const foundTee = findTee(foundCourse.name, r.tee);
        setClub(foundClub);
        setCourse(foundCourse);
        setTee(foundTee);
        setRoundForm({ name: r.name || '', date: r.date || '', firstTeeTime: r.firstTeeTime || '' });
        setEditingRound(r);
        setShowRoundForm(true);
    };

    const handleSubmitRound = async (e) => {
        e.preventDefault();
        if (!club.name) return setRoundError('Välj en klubb.');
        if (!course.name) return setRoundError('Välj en bana.');
        if (!roundForm.date) return setRoundError('Välj ett datum.');
        setRoundError(null);
        setSavingRound(true);
        const body = {
            name: roundForm.name,
            club: club.name,
            course: course.name,
            tee: tee.name,
            date: roundForm.date,
            firstTeeTime: roundForm.firstTeeTime,
        };
        try {
            if (editingRound) {
                const res = await apiFetch(`/league/${leagueId}/upcoming-round/${editingRound.id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) { const err = await res.json(); return setRoundError(err.error || 'Något gick fel.'); }
                const data = await res.json();
                setUpcomingRounds(prev => prev.map(r => r.id === editingRound.id ? data : r).sort(byDate));
            } else {
                const res = await apiFetch(`/league/${leagueId}/upcoming-rounds`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) { const err = await res.json(); return setRoundError(err.error || 'Något gick fel.'); }
                const data = await res.json();
                setUpcomingRounds(prev => [...prev, data].sort(byDate));
            }
            resetRoundForm();
            setShowRoundForm(false);
        } catch (err) {
            console.error(err);
            setRoundError('Något gick fel, försök igen.');
        } finally {
            setSavingRound(false);
        }
    };

    const handleDeleteRound = async (roundId) => {
        await apiFetch(`/league/${leagueId}/upcoming-round/${roundId}`, { method: 'DELETE' });
        setUpcomingRounds(prev => prev.filter(r => r.id !== roundId));
    };

    const handleUploadDoc = async (e) => {
        e.preventDefault();
        if (!docFile) return;
        setSavingDoc(true);
        try {
            const formData = new FormData();
            formData.append('document', docFile);
            const res = await apiFetch(`/league/${leagueId}/documents`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            setDocuments(prev => [data.document, ...prev]);
            setDocFile(null);
            setShowDocForm(false);
        } catch (err) {
            console.error(err);
        } finally {
            setSavingDoc(false);
        }
    };

    const handleDeleteDoc = async (docId) => {
        await apiFetch(`/league/${leagueId}/document/${docId}`, { method: 'DELETE' });
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
    };

    return (
        <div>
            <div className="hero-banner">
                <img src={norra16} alt="norra16" />
                <h2 className="hero-title">Allmäninformation</h2>
            </div>

            <div className="upcoming-rounds">
                <div className="section-header">
                    <div className="sub-header sub-header--with-img">
                        <CalendarCheck size="20" />
                        <h2>Kommande rundor</h2>
                    </div>
                    {isAdmin && (
                        <button className="add-btn" onClick={() => { resetRoundForm(); setShowRoundForm(v => !v); }}>
                            <Plus size="16" /> Lägg till
                        </button>
                    )}
                </div>

                {showRoundForm && (
                    <form className="round-form" onSubmit={handleSubmitRound}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Namn</label>
                                <input
                                    value={roundForm.name}
                                    onChange={e => setRoundForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder="t.ex. Singeltävling"
                                />
                            </div>
                            <div className="form-group form-group--date-select">
                                <label>Datum</label>
                                <input
                                    type="date"
                                    required
                                    value={roundForm.date}
                                    onChange={e => setRoundForm(p => ({ ...p, date: e.target.value }))}
                                />
                            </div>
                            <div className="form-group">
                                <label>Första starttid</label>
                                <input
                                    type="time"
                                    value={roundForm.firstTeeTime}
                                    onChange={e => setRoundForm(p => ({ ...p, firstTeeTime: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <span>Klubb</span>
                                <DropdownSelect
                                    items={clubs}
                                    value={club?.id || ""}
                                    onChange={handleClubChange}
                                    placeholder="Välj klubb"
                                />
                            </div>
                            <div className="form-group">
                                <span>Bana</span>
                                <DropdownSelect
                                    items={courses[club?.name] || []}
                                    value={course?.id || ""}
                                    onChange={handleCourseChange}
                                    placeholder="Välj bana"
                                />
                            </div>
                            <div className="form-group">
                                <span>Tee</span>
                                <DropdownSelect
                                    items={tees[course?.name] || []}
                                    value={tee?.id || ""}
                                    onChange={setTee}
                                    placeholder="Välj tee"
                                />
                            </div>
                        </div>
                        {roundError && <p className="form-error">{roundError}</p>}
                        <div className="form-actions">
                            <button type="submit" className="button button--standard button--save" disabled={savingRound}>
                                {savingRound ? <Loader size="14" className="spinner" /> : 'Spara'}
                            </button>
                            <button type="button" className="button button--standard" onClick={() => { resetRoundForm(); setShowRoundForm(false); }}>
                                Avbryt
                            </button>
                        </div>
                    </form>
                )}

                <div className="round-list">
                    {upcomingRounds.length === 0 && (
                        <p className="empty-state">Inga kommande rundor</p>
                    )}
                    {upcomingRounds.map(round => (
                        <div key={round.id} className={`round-item${isPast(round.date) ? ' round-item--played' : ''}`}>
                            <div className="round-details">
                                {round.name && <span className="round-name">{round.name}</span>}
                                <span>{round.club} · {round.course}{round.tee && round.tee !== 'Ej bestämt' ? ` · ${round.tee}` : ''}</span>
                                <span className="round-date">
                                    {round.date}{round.firstTeeTime ? ` · ${round.firstTeeTime}` : ''}
                                </span>
                            </div>
                            {isAdmin && (
                                <div className="round-actions">
                                    <button className="icon-btn" onClick={() => handleEditClick(round)}>
                                        <Pencil size="14" />
                                    </button>
                                    <button className="icon-btn icon-btn--delete" onClick={() => handleDeleteRound(round.id)}>
                                        <X size="14" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="info-documents">
                <div className="section-header">
                    <div className="sub-header sub-header--with-img">
                        <Folder size="20" />
                        <h2>Dokument</h2>
                    </div>
                    {isAdmin && (
                        <button className="add-btn" onClick={() => setShowDocForm(v => !v)}>
                            <Plus size="16" /> Ladda upp
                        </button>
                    )}
                </div>

                {showDocForm && (
                    <form className="doc-form" onSubmit={handleUploadDoc}>
                        <div className="form-group">
                            <label>Välj fil (.pdf, .doc, .docx)</label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                required
                                onChange={e => setDocFile(e.target.files[0] || null)}
                            />
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="button button--standard button--save" disabled={savingDoc || !docFile}>
                                {savingDoc ? <Loader size="14" className="spinner" /> : 'Ladda upp'}
                            </button>
                            <button type="button" className="button button--standard" onClick={() => { setShowDocForm(false); setDocFile(null); }}>
                                Avbryt
                            </button>
                        </div>
                    </form>
                )}

                <div className="doc-list">
                    {documents.length === 0 && (
                        <p className="empty-state">Inga dokument</p>
                    )}
                    {documents.map(doc => (
                        <div key={doc.id} className="doc-item">
                            <a href={doc.url} target="_blank" rel="noreferrer" className="doc-link">
                                📄 {doc.fileName}
                            </a>
                            {isAdmin && (
                                <button className="icon-btn icon-btn--delete" onClick={() => handleDeleteDoc(doc.id)}>
                                    <X size="14" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
